import type { GetExperimentAnalysisQueryParams } from '@gonfalon/openapi/operations/getExperimentAnalysis';
import type { ExperimentAnalysis, schemas } from '@gonfalon/openapi/types';
import type { QueryOptions } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';

import { createQueryHook } from '../internal/createQueryHook';
import { experiments } from '../queries/experiments';

/**
 * @deprecated Use `experiments.detail()._ctx.analysis()` with `useQuery` directly.
 */
export const useExperimentAnalysis = createQueryHook(experimentAnalysisQuery);

export function useExperimentAnalyses(
  {
    projectKey,
    environmentKey,
    experimentKey,
    slices,
    params,
    enabled,
  }: {
    projectKey: string;
    environmentKey: string;
    experimentKey: string;
    slices: Array<{ metricKey: string; attribute?: { name: string; value: string | number | boolean } }>;
    params?: GetExperimentAnalysisQueryParams;
    enabled?: boolean;
  },
  options?: QueryOptions,
) {
  return useQueries({
    ...options,
    queries: slices.map(({ metricKey, attribute }) => ({
      ...experimentAnalysisQuery({
        projectKey,
        environmentKey,
        experimentKey,
        metricKey,
        params: {
          ...params,
          attributes: attribute ? [{ name: attribute.name, values: [attribute.value] }] : undefined,
        },
      }),
      enabled,
    })),
  });
}

function experimentAnalysisQuery({
  projectKey,
  environmentKey,
  experimentKey,
  metricKey,
  params,
}: {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
  metricKey: string;
  params?: GetExperimentAnalysisQueryParams;
}) {
  return {
    ...experiments.detail({ projectKey, environmentKey, experimentKey })._ctx.analysis({ metricKey, params }),
    select: experimentAnalysisSelect,
  };
}

function experimentAnalysisSelect(analysis: schemas['ExperimentAnalysisRep']): ExperimentAnalysis {
  return {
    ...analysis,
    treatments: (analysis.treatments ?? []).map(
      // eslint-disable-next-line array-callback-return
      (treatment: schemas['TreatmentAnalysisRep']) => {
        const { model, statisticalAnalysisMetadata, ...rest } = treatment;
        if (model === 'bayesianBeta') {
          const bayesianBeta = 'bayesianBeta' in treatment ? treatment.bayesianBeta : undefined;
          if (!bayesianBeta && !statisticalAnalysisMetadata?.skippedStatisticalAnalysis) {
            throw new Error('Bayesian beta treatment analysis is missing');
          }
          return {
            ...rest,
            statisticalAnalysisMetadata,
            model,
            modelAnalysis: bayesianBeta ?? {},
            methodology: 'bayesian',
            pdf: 'beta',
          };
        }
        if (model === 'bayesianNormal') {
          const bayesianNormal = 'bayesianNormal' in treatment ? treatment.bayesianNormal : undefined;
          if (!bayesianNormal && !statisticalAnalysisMetadata?.skippedStatisticalAnalysis) {
            throw new Error('Bayesian normal treatment analysis is missing');
          }
          return {
            ...rest,
            statisticalAnalysisMetadata,
            model,
            modelAnalysis: bayesianNormal ?? {},
            methodology: 'bayesian',
            pdf: 'normal',
          };
        }
        if (model === 'frequentistBeta') {
          const frequentistBeta = 'frequentistBeta' in treatment ? treatment.frequentistBeta : undefined;
          if (!frequentistBeta && !statisticalAnalysisMetadata?.skippedStatisticalAnalysis) {
            throw new Error('Frequentist beta treatment analysis is missing');
          }
          return {
            ...rest,
            statisticalAnalysisMetadata,
            model,
            modelAnalysis: frequentistBeta ?? {},
            methodology: 'frequentist',
            pdf: 'beta',
          };
        }
        if (model === 'frequentistNormal') {
          const frequentistNormal = 'frequentistNormal' in treatment ? treatment.frequentistNormal : undefined;
          if (!frequentistNormal && !statisticalAnalysisMetadata?.skippedStatisticalAnalysis) {
            throw new Error('Frequentist normal treatment analysis is missing');
          }
          return {
            ...rest,
            statisticalAnalysisMetadata,
            model,
            modelAnalysis: frequentistNormal ?? {},
            methodology: 'frequentist',
            pdf: 'normal',
          };
        }
        throw new Error(`Unknown treatment model: ${model}`);
      },
    ),
  };
}
